<!-- GooglePayWidget.vue -->
<template>
  <div class="google-pay-widget"
       v-if="isSupported"
       ref="googlePayButton"
       @click="onGooglePayClicked">
  </div>
</template>

<script>
import { loadGooglePaySDK } from '@vue/utils/devicePayLoader'
import httpCommerce from '@vue/services/api/httpCommerce'
import { getToken } from '@vue/services/jwt.service'

const GOOGLE_PAY_API_VERSION      = 2;
const GOOGLE_PAY_API_VERSION_MINOR = 0;
const PAYMENT_CANCELLED = 'CANCELED';
export default {
  name: 'GooglePayWidget',

  props: {
    amount: {
      type: String,
      required: true,
    },
    currencyCode: {
      type: String,
      default: 'GBP',
    },
    countryCode: {
      type: String,
      default: 'GB',
    },
    acceptCreditCards: {
      type: Boolean,
      required: true
    },
    merchantId: {
      type: String,
      required: true,
    },
    merchantName: {
      type: String,
      required: true,
    },
    gatewayMerchant: {
      type: String,
      required: true
    },
    // Your SmartPay Advance merchant account name
    gatewayMerchantId: {
      type: String,
      required: true,
    },
    environment: {
      type: String,
      required: false,
      default: 'TEST'
    }
  },

  data() {
    return {
      isSupported: false,
      isProcessing: false
    };
  },

  async mounted() {
    try {
      await loadGooglePaySDK();
      this.$paymentsClient = this.createPaymentsClient();
      this.isSupported = await this.checkGooglePaySupport();

      if (this.isSupported) {
        this.$nextTick(this.renderGooglePayButton);
      }
    } catch (err) {
      console.error('Google Pay initialisation failed:', err);
      this.isSupported = false;
    } finally {
      this.$emit('available', this.isSupported);
    }
  },

  beforeDestroy() {
    this.$paymentsClient = null;
  },

  methods: {
    waitForParent(eventName) {
      return new Promise((resolve, reject) => {
        this.$emit(eventName, {
          ...null,
          resolve,
          reject
        });
      });
    },

    createPaymentsClient() {
      return new google.payments.api.PaymentsClient({
        environment: this.environment,
      });
    },

    async checkGooglePaySupport() {
      const { result } = await this.$paymentsClient.isReadyToPay({
        apiVersion: GOOGLE_PAY_API_VERSION,
        apiVersionMinor: GOOGLE_PAY_API_VERSION_MINOR,
        allowedPaymentMethods: [this.buildAllowedPaymentMethods()],
      });

      return result;
    },

    renderGooglePayButton() {
      const button = this.$paymentsClient.createButton({
        onClick: this.onGooglePayClicked,
        buttonType: 'buy',
        buttonColor: 'black',
      });
      this.$refs.googlePayButton.appendChild(button);
    },

    buildAllowedPaymentMethods() {
      return {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: ['MASTERCARD', 'VISA'],
          allowCreditCards: this.acceptCreditCards ?? false
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: this.gatewayMerchant,
            gatewayMerchantId: this.gatewayMerchantId,
          },
        },
      };
    },

    buildPaymentRequest() {
      return {
        apiVersion: GOOGLE_PAY_API_VERSION,
        apiVersionMinor: GOOGLE_PAY_API_VERSION_MINOR,
        allowedPaymentMethods: [this.buildAllowedPaymentMethods()],
        merchantInfo: {
          merchantId:   this.merchantId,
          merchantName: this.merchantName,
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: this.amount,
          currencyCode: this.currencyCode,
          countryCode: this.countryCode,
        },
      };
    },

    async onGooglePayClicked() {
      if (!this.$paymentsClient || this.isProcessing) {
        return;
      }
      this.$emit('click');
      this.isProcessing = true;

      try {
        await this.waitForParent('confirmOrder');
        const paymentData = await this.$paymentsClient.loadPaymentData(
          this.buildPaymentRequest()
        );
        await this.onPaymentAuthorized(paymentData);
      } catch (err) {
        // Google Pay uses a rejection with statusCode CANCELED for dismissal
        if (err?.statusCode === PAYMENT_CANCELLED) {
          this.onCancel();
        } else if (err) {
          this.$emit('error', { stage: 'loadPaymentData', error: err });
        }
        // If err is falsy, confirmOrder was rejected — error already handled by the parent
      } finally {
        this.isProcessing = false;
      }
    },

    async onPaymentAuthorized(paymentData) {
      if (!this.$paymentsClient) return;

      try {
        const token = paymentData.paymentMethodData.tokenizationData.token;
        const data = await this.processPaymentOnServer(token);
        if (data.redirectUrl) {
          this.$emit('3ds', { url: data.redirectUrl });
        } else {
          this.$emit('success', data);
        }
      } catch (err) {
        this.$emit('error', { stage: 'paymentAuthorized', error: err });
      }
    },

    onCancel() {
      this.$emit('cancel');
    },

    async processPaymentOnServer(token) {
      const bearerToken = await getToken();
      const { data } = await httpCommerce.post(
        'device-payment/process-google-payment',
        {
          token,
          amount: this.amount,
          currencyCode: this.currencyCode,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${bearerToken}`,
          }
        });

      return data;
    },
  },
};
</script>
